import React from 'react';
import classNameBind from 'classnames/bind';
import _debounce from 'lodash/debounce';
import moment from 'moment';
import { formatDate, formatToOptions, getLangForLocale } from '../../../../formatters';
import { withTranslation } from '../../../Translator';
import Stack from '../../../Stack';
import Checkbox from '../../../Checkbox';
import { TextField } from '../../../TextField';
import Button from '../../../Button';
import Divider from '../../../Divider';
import Tabs from '../../../Tabs';
import Information from '../../../Information';
import Spacer from '../../../Spacer';
import { CUSTOM, DAY, MONTH, WEEK, QUARTER } from '../../timePeriods';
import dateIsValid from '../../methods/dateIsValid';
import { Select } from '../../../Select';
import { getOptions, timePeriodsCurrent, timePeriodsCustom, timePeriodsLast } from './options';
import './locales';
import styles from './TimePeriod.module.css';

const bindClasses = classNameBind.bind(styles);

const DEBOUNCE_WAIT_TIME = 850;

class TimePeriod extends React.Component {
  constructor(props) {
    super(props);

    const {
      startDate: rawStartDate,
      endDate: rawEndDate,
      i18n,
      comparisonStart: rawComparisonStartDate,
      comparisonEnd: rawComparisonEndDate,
      t,
    } = props;

    this.propagateData = _debounce(this.onSubmit, DEBOUNCE_WAIT_TIME);
    this.propagateComparisonStartData = _debounce(this.onComparisonSubmit, DEBOUNCE_WAIT_TIME);
    this.propagateComparisonEndData = _debounce(this.onComparisonSubmit, DEBOUNCE_WAIT_TIME);
    this.comparisionOptions = getOptions(t);
    this.timePeriodsCustom = timePeriodsCustom(t);
    this.timePeriodsCurrent = timePeriodsCurrent(t);
    this.timePeriodsLast = timePeriodsLast(t);

    let startDate = '';
    let endDate = '';
    let comparisonStart = '';
    let comparisonEnd = '';
    if (dateIsValid(rawStartDate)) {
      startDate = formatDate(rawStartDate.toDate(), i18n.language, formatToOptions('L'));
    }

    if (dateIsValid(rawEndDate)) {
      endDate = formatDate(rawEndDate.toDate(), i18n.language, formatToOptions('L'));
    }

    if (dateIsValid(rawComparisonStartDate)) {
      comparisonStart = formatDate(rawComparisonStartDate.toDate(), i18n.language, formatToOptions('L'));
    }

    if (dateIsValid(rawComparisonEndDate)) {
      comparisonEnd = formatDate(rawComparisonEndDate.toDate(), i18n.language, formatToOptions('L'));
    }

    this.state = {
      startDate,
      endDate,
      comparisonStart,
      comparisonEnd,
      validStartDate: true,
      validEndDate: true,
      validComparisonEndDate: true,
      validComparisonStartDate: true,
      activeTab: undefined,
      activeId: this.props.activeId || 'custom',
    };
  }

  componentDidUpdate(prevProps) {
    if (((prevProps.startDate !== this.props.startDate) && this.props.startDate)
      || ((prevProps.endDate !== this.props.endDate) && this.props.endDate)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        startDate: formatDate(this.props.startDate.toDate(), this.props.i18n.language, formatToOptions('L')),
        endDate: formatDate(this.props.endDate.toDate(), this.props.i18n.language, formatToOptions('L')),
      });
    }
    if (((prevProps.comparisonStart !== this.props.comparisonStart) && this.props.comparisonStart)
      || ((prevProps.comparisonEnd !== this.props.comparisonEnd) && this.props.comparisonEnd)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        comparisonStart: formatDate(this.props.comparisonStart.toDate(), this.props.i18n.language, formatToOptions('L')),
        comparisonEnd: formatDate(this.props.comparisonEnd.toDate(), this.props.i18n.language, formatToOptions('L')),
      });
    }

    if (prevProps.activeId && this.props.activeId && prevProps.activeId !== this.props.activeId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        activeId: this.props.activeId,
        activeTab: this.activeTabDecided(this.props.activeId),
      });
    }
  }

  onStartDateChange = (event) => {
    this.setState({
      startDate: event.target.value,
      validStartDate: true,
    }, this.propagateData);
  };

  onEndDateChange = (event) => {
    this.setState({
      endDate: event.target.value,
      validEndDate: true,
    }, this.propagateData);
  };

  onComparisonStartDateChange = (event) => {
    this.setState({
      comparisonStart: event.target.value,
      validComparisonStartDate: true,
    }, this.propagateComparisonStartData);
  };

  onComparisonEndDateChange = (event) => {
    this.setState({
      comparisonEnd: event.target.value,
      validComparisonEndDate: true,
    }, this.propagateComparisonEndData);
  };

  onSubmit = () => {
    const { startDate: rawStartDate, endDate: rawEndDate } = this.state;
    const { min, max, i18n } = this.props;
    const startDate = moment(rawStartDate, 'L', getLangForLocale(i18n.language));
    const endDate = moment(rawEndDate, 'L', getLangForLocale(i18n.language));
    let validStartDate = true;
    let validEndDate = true;
    const reformattedDates = {};
    const startDayLengthYearValid = rawStartDate.length >= 8
      && startDate.year() >= min.year();
    const endDayLengthYearValid = rawEndDate.length >= 8
      && endDate.year() >= min.year() && endDate.year() <= max.year();
    if (startDayLengthYearValid) {
      validStartDate = startDate.isValid();
      if (validStartDate) {
        reformattedDates.startDate = formatDate(startDate.toDate(), i18n.language, formatToOptions('L'));
      }
    } else {
      validStartDate = false;
    }

    if (endDayLengthYearValid) {
      validEndDate = endDate.isValid() && (!validStartDate || endDate.isSameOrAfter(startDate));
      if (validEndDate) {
        reformattedDates.endDate = formatDate(endDate.toDate(), i18n.language, formatToOptions('L'));
      }
    } else {
      validEndDate = false;
    }

    this.setState({
      ...reformattedDates,
      validStartDate,
      validEndDate,
    });

    if (validStartDate && validEndDate && startDayLengthYearValid && endDayLengthYearValid) {
      this.props.onDateRangeChange(startDate, endDate);
    }
  };

  onComparisonSubmit = () => {
    const {
      startDate: rawStartDate,
      endDate: rawEndDate,
      comparisonStart: rawComparisonStartDate,
      comparisonEnd: rawComparisonEndDate,
    } = this.state;
    const { min, max, i18n } = this.props;
    const startDate = moment(rawStartDate, 'L', getLangForLocale(i18n.language));
    const endDate = moment(rawEndDate, 'L', getLangForLocale(i18n.language));
    const comparisonStart = moment(rawComparisonStartDate, 'L', getLangForLocale(i18n.language));
    const comparisonEnd = moment(rawComparisonEndDate, 'L', getLangForLocale(i18n.language));
    let validComparisonStartDate = true;
    let validComparisonEndDate = true;
    const reformattedDates = {};
    const startDayLengthYearValid = rawStartDate.length >= 8
      && startDate.year() >= min.year();
    const endDayLengthYearValid = rawEndDate.length >= 8
      && endDate.year() >= min.year() && endDate.year() <= max.year();

    if (startDayLengthYearValid) {
      validComparisonStartDate = (
        comparisonStart.isValid() && comparisonStart.year() >= min.year() && comparisonStart.year() <= max.year()
      );
      if (validComparisonStartDate) {
        reformattedDates.comparisonStart = formatDate(comparisonStart.toDate(), i18n.language, formatToOptions('L'));
      }
    } else {
      validComparisonStartDate = false;
    }

    if (endDayLengthYearValid) {
      validComparisonEndDate = (
        comparisonEnd.isValid() && comparisonEnd.year() >= min.year() && comparisonEnd.year() <= max.year()
      );
      if (validComparisonEndDate) {
        reformattedDates.comparisonEnd = formatDate(comparisonEnd.toDate(), i18n.language, formatToOptions('L'));
      }
    } else {
      validComparisonEndDate = false;
    }

    this.setState({
      ...reformattedDates,
      validComparisonStartDate,
      validComparisonEndDate,
    }, () => {
      if (
        validComparisonStartDate
        && validComparisonEndDate
        && startDayLengthYearValid
        && endDayLengthYearValid
      ) {
        this.props.onComparisonRangeChange(comparisonStart, comparisonEnd);
      } else if (
        validComparisonStartDate && startDayLengthYearValid) {
        this.props.onComparisonRangeChange(comparisonStart, null);
      } else if (
        validComparisonEndDate && endDayLengthYearValid) {
        this.props.onComparisonRangeChange(null, comparisonEnd);
      }
    });
  };

   activeTabDecided = (activeId) => {
     if (this.timePeriodsLast.find(el => el.id === activeId)) {
       return 'last';
     }
     if (this.timePeriodsCustom.find(el => el.id === activeId)) {
       return 'custom';
     }

     return 'current';
   };

   render() {
     const {
       period,
       onTimePeriodChange,
       onComparisonEnabledChange,
       onComparisonModeChange,
       t,
       comparisonModeVisible,
       comparisonMode,
       comparisonEnabled,
       dataId,
       changeMode,
       toggleFocus,
       focusedInput,
       children,
       popoverRef,
       onApply,
       autoSubmit,
       isDemoSite,
     } = this.props;
     const {
       startDate,
       endDate,
       comparisonStart,
       comparisonEnd,
       validStartDate,
       validEndDate,
       validComparisonStartDate,
       validComparisonEndDate,
       activeId,
     } = this.state;
     const disabled = period !== CUSTOM;

     return (
       <div className={bindClasses('wrapper')} data-ppuc-id={dataId}>
         <Stack>
           <Stack.Item>
             <Stack vertical>
               {isDemoSite && (
               <Stack.Item fill previousSpacing="wide">
                 <Information type="info">
                   {t('reportdatepickerDemoWarning')}
                 </Information>
                 <Spacer size={0.5} />
               </Stack.Item>
               )}
               <Stack.Item fill previousSpacing={isDemoSite ? 'narrow' : 'default'}>
                 <div className={bindClasses('customRangeInputs')} data-ppuc-id={`${dataId}.range`}>
                   <TextField
                     name="startDate"
                     disabled={disabled}
                     touched
                     error={validStartDate ? '' : t('date.invalid')}
                     onChange={this.onStartDateChange}
                     value={startDate}
                     onFocus={() => { changeMode('normal'); toggleFocus(1); }}
                     appearance="date-range"
                     forceOutline={focusedInput === 1}
                     label={t('reportdatepickerRangeFrom')}
                   />
                   <TextField
                     name="endDate"
                     disabled={disabled}
                     touched
                     error={validEndDate ? '' : t('date.invalid')}
                     onChange={this.onEndDateChange}
                     value={endDate}
                     onFocus={() => { changeMode('normal'); toggleFocus(2); }}
                     appearance="date-range"
                     forceOutline={focusedInput === 2}
                     label={t('reportdatepickerRangeTo')}
                   />
                 </div>
               </Stack.Item>
             </Stack>
             <Stack vertical>
               <Stack.Item>
                 {children}
               </Stack.Item>
             </Stack>
             {comparisonModeVisible && (
             <div className={bindClasses('comparison-wrapper')} data-ppuc-id={`${dataId}.compare`}>
               <Stack verticalAlignment="center" spacing="extraNarrow">
                 <Stack.Item>
                   <Checkbox
                     name="compare_to"
                     label={t('compareTo')}
                     checked={comparisonEnabled}
                     onChange={(event) => {
                       if (comparisonMode === 'custom_range' && event.target.checked) {
                         changeMode('compare');
                       } else {
                         changeMode('normal');
                       }
                       onComparisonEnabledChange(event);
                     }}
                   />
                 </Stack.Item>
                 <Stack.Item>
                   <Select
                     name="compare_to_period"
                     options={[{
                       id: 0,
                       name: '',
                       items: [DAY, MONTH, WEEK, QUARTER, CUSTOM].indexOf(period) !== -1 ? this.comparisionOptions : [this.comparisionOptions[0], this.comparisionOptions[1]],
                     }]}
                     value={comparisonMode}
                     type="subdued"
                     onChange={(event) => {
                       if (event.target.value === 'custom_range') {
                         changeMode('compare');
                       } else {
                         changeMode('normal');
                       }
                       onComparisonModeChange(event.target.value);
                     }}
                     disabled={!comparisonEnabled}
                     key={`${period}_${comparisonMode}`}
                   />
                 </Stack.Item>
               </Stack>
               {comparisonEnabled && (
               <div className={bindClasses('customRangeInputs')} data-ppuc-id={`${dataId}.compareRange`}>
                 <TextField
                   name="startRangeDate"
                   disabled={comparisonMode !== 'custom_range'}
                   touched
                   error={validComparisonStartDate ? '' : t('date.invalid')}
                   value={comparisonStart}
                   onChange={this.onComparisonStartDateChange}
                   onFocus={() => { changeMode('compare'); toggleFocus(3); }}
                   appearance="compare-date-range"
                   forceOutline={focusedInput === 3}
                   label={t('reportdatepickerRangeFrom')}
                 />
                 <TextField
                   name="endRangeDate"
                   disabled={comparisonMode !== 'custom_range'}
                   touched
                   error={validComparisonEndDate ? '' : t('date.invalid')}
                   value={comparisonEnd}
                   onChange={this.onComparisonEndDateChange}
                   onFocus={() => { changeMode('compare'); toggleFocus(4); }}
                   appearance="compare-date-range"
                   forceOutline={focusedInput === 4}
                   label={t('reportdatepickerRangeTo')}
                 />
               </div>
               )}
             </div>
             )}
           </Stack.Item>
           <div className={bindClasses('sh-ctn')}>
             <Tabs
               tabs={[
                 {
                   id: 1,
                   label: t('reportdatepickerRangeLast'),
                   value: 'last',
                 }, {
                   id: 2,
                   label: t('reportdatepickerRangeCurrent'),
                   value: 'current',
                 }, {
                   id: 3,
                   label: t('reportdatepickerRangeCustom'),
                   value: 'custom',
                 },
               ]}
               active={this.state.activeTab || this.activeTabDecided(activeId)}
               setActive={(val) => {
                 this.setState({ activeTab: val });
               }}
             />
             <div className={bindClasses('shortcut-list')} style={{ paddingTop: '8px' }}>
               {({ custom: this.timePeriodsCustom, current: this.timePeriodsCurrent, last: this.timePeriodsLast }[this.state.activeTab || this.activeTabDecided(activeId)]).map(el => (
                 <div key={el.id}>
                   <Button
                     fullWidth
                     appearance={activeId === el.id ? 'simple' : 'subdued'}
                     text={el.name}
                     onClick={() => {
                       if ([DAY, MONTH, WEEK, QUARTER, CUSTOM].indexOf(el.value) === -1 && comparisonMode === 'custom_range') {
                         onComparisonModeChange('previous_time');
                       }
                       onTimePeriodChange(el);
                       this.setState({
                         activeId: el.id,
                         validEndDate: true,
                         validStartDate: true,
                         validComparisonStartDate: true,
                         validComparisonEndDate: true,
                       });
                     }}
                   />
                 </div>
               ))}
             </div>
             <React.Fragment>
               <Divider />
               <div style={{ marginBottom: '8px' }}>
                 <Stack>
                   <Stack.Item fill>
                     {!autoSubmit && (
                     <Button
                       fullWidth
                       text={t('cancel')}
                       onClick={() => {
                         popoverRef.current.onToggle();
                       }}
                     />
                     )}
                   </Stack.Item>
                   <Stack.Item fill>
                     <Button
                       fullWidth
                       appearance="primary"
                       text={t('apply')}
                       disabled={(comparisonEnabled && (!validComparisonEndDate || !validComparisonStartDate)) || !validStartDate || !validEndDate}
                       onClick={
                         () => (
                           autoSubmit ? popoverRef.current.onToggle() : onApply(() => { popoverRef.current.onToggle(); })
                         )
                       }
                     />
                   </Stack.Item>
                 </Stack>
               </div>
             </React.Fragment>
           </div>
         </Stack>
       </div>
     );
   }
}

TimePeriod.defaultProps = {
  period: DAY,
  startDate: null,
  endDate: null,
  comparisonStart: undefined,
  comparisonEnd: undefined,
  changeMode: () => {},
  dataId: 'TimePeriod',
  toggleFocus: () => {},
  activeId: 'custom',
  children: null,
  focusedInput: 0,
  onApply: () => {},
};

export const PureComponent = TimePeriod;
export default withTranslation('components')(TimePeriod);
